/* eslint-disable max-len */
/* eslint-disable no-dupe-else-if */
import React, { useEffect, useState } from 'react';
import { navigate } from '@gatsbyjs/reach-router';
import { useSelector } from 'react-redux';

import { getOrder } from '@api/backendAPI';
import { getLanguagePrefix } from '@helpers';
import {
  PAYMENT_SOURCE,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
  PAYMENT_METHOD,
  PAYMENT_REASON,
} from '@constants/payments';

export const OrderDetailsContext = React.createContext();

export const OrderDetailsProvider = ({ value, children }) => {
  const { error, total, balance, payments, sfOrder } = value;

  return (
    <OrderDetailsContext.Provider
      value={{ error, total, balance, payments, sfOrder }}
    >
      {children}
    </OrderDetailsContext.Provider>
  );
};

export const achDefaultValue = {
  paymentStatus: 'Processing',
  paymentAmount: 0,
};

export const useOrderDetails = (
  orderId,
  setTotal,
  setBalance,
  setPayments,
  setSfOrder,
  setHasPendingPayments,
  setHasPendingACHPayments,
  setOrderFetchCompleted,
  setError,
  setAvailableAchLimit,
  setAvailableCardLimit,
  orderFetchCompleted
) => {
  if (orderFetchCompleted) return;

  const REACT_APP_ACH_PAYMENT_LIMIT =
    process.env.REACT_APP_ACH_PAYMENT_LIMIT || 15000;
  const REACT_APP_CARD_PAYMENT_LIMIT =
    process.env.REACT_APP_CARD_PAYMENT_LIMIT || 8000;
  const REACT_APP_CARD_PAYMENT_LIMIT_US =
    process.env.REACT_APP_CARD_PAYMENT_LIMIT_US || 15000;
  const REACT_APP_CARD_PAYMENT_PER_TRANSACTION =
    process.env.REACT_APP_CARD_PAYMENT_PER_TRANSACTION || 15000;
  const REACT_APP_ACSS_DEBIT_PAYMENT_LIMIT =
    process.env.REACT_APP_ACSS_DEBIT_PAYMENT_LIMIT || 15000;

  getOrder(orderId)
    .then(({ total, payments, sfOrder }) => {
      setTotal(total);
      setPayments(payments);
      const totalPayments = sfOrder.payments.reduce((acc, payment) => {
        if (
          payment.paymentType === PAYMENT_TYPE.PAYMENT &&
          (payment.paymentStatus === PAYMENT_STATUS.SUCCEEDED ||
            payment.paymentStatus === PAYMENT_STATUS.PENDING)
        ) {
          acc += payment.paymentAmount;
        } else if (
          payment.paymentType === PAYMENT_TYPE.REFUND &&
          payment.paymentStatus === PAYMENT_STATUS.SUCCEEDED
        ) {
          acc -= payment.paymentAmount;
        }
        return acc;
      }, 0);
      setSfOrder({ ...sfOrder, totalPayments });
      const balance = {
        amount:
          sfOrder.orderTotal -
          (sfOrder.financeApprovedAmount || 0) -
          (sfOrder.acquisitionAmountRemaining || 0) -
          totalPayments,
        currency: sfOrder.currencyCode,
      };
      setBalance(balance);
      if (
        sfOrder.payments &&
        payments.some(
          ({ paymentStatus }) => paymentStatus === PAYMENT_STATUS.PENDING
        )
      ) {
        setHasPendingPayments(false);
      }
      const pendingCondition = ({ paymentStatus, paymentMethod }) =>
        paymentMethod === 'ACH' &&
        ['processing', 'pending'].includes(paymentStatus.toLowerCase());
      const isPrevACHPaymentProcessing =
        sfOrder?.payments?.find(pendingCondition) || achDefaultValue;

      if (isPrevACHPaymentProcessing.paymentAmount) {
        isPrevACHPaymentProcessing.paymentAmount = sfOrder?.payments
          ?.filter(pendingCondition)
          .reduce((total, current) => total + current.paymentAmount, 0);
      }

      setHasPendingACHPayments(isPrevACHPaymentProcessing);
      const totalAchPaidAmount = sfOrder.payments
        .filter(p => p.paymentMethod === PAYMENT_METHOD.ACH)
        .reduce((total, current) => {
          if (
            current.paymentType === PAYMENT_TYPE.PAYMENT &&
            (current.paymentStatus === PAYMENT_STATUS.SUCCEEDED ||
              current.paymentStatus === PAYMENT_STATUS.PENDING)
          ) {
            return total + current.paymentAmount;
          } else if (
            current.paymentType === PAYMENT_TYPE.REFUND &&
            current.paymentStatus === PAYMENT_STATUS.SUCCEEDED
          ) {
            return total - current.paymentAmount;
          }
          return total;
        }, 0);
      const totalCardPaidAmount = sfOrder.payments
        .filter(
          p =>
            p.paymentMethod === PAYMENT_METHOD.CARD &&
            p.paymentReason !== PAYMENT_REASON.DEPOSIT
        )
        .reduce((total, current) => {
          if (
            current.paymentType === PAYMENT_TYPE.PAYMENT &&
            (current.paymentStatus === PAYMENT_STATUS.SUCCEEDED ||
              current.paymentStatus === PAYMENT_STATUS.PENDING)
          ) {
            return total + current.paymentAmount;
          } else if (
            current.paymentType === PAYMENT_TYPE.REFUND &&
            current.paymentStatus === PAYMENT_STATUS.SUCCEEDED
          ) {
            return total - current.paymentAmount;
          }
          return total;
        }, 0);
      const achLimit = Math.max(
        Math.min(
          Math.round(
            100 *
              Math.max(
                (sfOrder.currencyCode?.toLowerCase() === 'cad'
                  ? REACT_APP_ACSS_DEBIT_PAYMENT_LIMIT
                  : REACT_APP_ACH_PAYMENT_LIMIT) - totalAchPaidAmount,
                0
              )
          ) / 100,
          balance.amount
        ),
        0
      );
      const cardLimitCountryBase =
        sfOrder.currencyCode?.toLowerCase() === 'cad'
          ? REACT_APP_CARD_PAYMENT_LIMIT
          : REACT_APP_CARD_PAYMENT_LIMIT_US;
      const cardLimit = Math.max(
        Math.min(
          cardLimitCountryBase - totalCardPaidAmount,
          balance.amount,
          REACT_APP_CARD_PAYMENT_PER_TRANSACTION
        ),
        0
      );
      setAvailableAchLimit(achLimit);
      setAvailableCardLimit(cardLimit);
      setError(false);
      setOrderFetchCompleted(true);
    })
    .catch(e => {
      setError(true);
      setOrderFetchCompleted(true);
    });

  return null;
};
